import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { Package, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MyOrdersPage = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const response = await axios.get(`${API}/api/orders`, {
                });
                setOrders(response.data);
            } catch {
                toast.error("Errore nel caricamento degli ordini");
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, []);

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'badge-warning',
            confirmed: 'badge-success',
            shipped: 'badge-primary',
            delivered: 'badge-success',
            cancelled: 'badge-danger'
        };
        const labels = {
            pending: 'In attesa',
            confirmed: 'Confermato',
            shipped: 'Spedito',
            delivered: 'Consegnato',
            cancelled: 'Annullato'
        };
        return <span className={`badge ${styles[status] || 'badge-muted'}`}>{labels[status] || status}</span>;
    };

    const getPaymentBadge = (status) => {
        return status === 'paid' 
            ? <span className="badge badge-success">Pagato</span>
            : <span className="badge badge-warning">In attesa</span>;
    };

    if (loading) {
        return (
            <div className="min-h-screen py-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold mb-8">I miei ordini</h1>
                    <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-surface rounded-xl h-40 animate-pulse" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-3xl font-bold mb-8" data-testid="orders-title">I miei ordini</h1>

                {orders.length === 0 ? (
                    <div className="text-center py-16 bg-surface rounded-xl border border-border">
                        <ShoppingBag className="w-16 h-16 mx-auto text-muted mb-4" />
                        <h2 className="text-xl font-semibold mb-2">Nessun ordine</h2>
                        <p className="text-muted mb-6">Non hai ancora effettuato ordini</p>
                        <Link to="/store">
                            <Button className="btn-primary">
                                Vai allo store
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {orders.map((order) => (
                            <div
                                key={order.id}
                                className="bg-surface border border-border rounded-xl p-6"
                                data-testid={`order-${order.id}`}
                            >
                                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                                    <div>
                                        <p className="text-sm text-muted">Ordine #{order.id.slice(0, 8)}</p>
                                        <p className="text-sm text-muted">
                                            {format(parseISO(order.created_at), 'dd MMMM yyyy, HH:mm', { locale: it })}
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        {getPaymentBadge(order.payment_status)}
                                        {getStatusBadge(order.status)}
                                    </div>
                                </div>

                                <div className="space-y-3 mb-4">
                                    {order.items.map((item, idx) => (
                                        <div key={`${item.product_id}-${idx}`} className="flex items-center gap-3">
                                            <Package className="w-5 h-5 text-primary flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium truncate">{item.name}</p>
                                                <p className="text-sm text-muted">Qtà: {item.quantity} x €{item.price.toFixed(2)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex justify-between items-center pt-4 border-t border-border">
                                    <div className="text-sm text-muted">
                                        <p>{order.shipping_address}</p>
                                        <p>{order.shipping_postal_code} {order.shipping_city}</p>
                                    </div>
                                    <p className="text-xl font-bold text-primary">€{order.total.toFixed(2)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyOrdersPage;
